import React, { useEffect, useState } from "react";
import { getCustomers, saveCustomer, searchCustomers, bulkImportCustomers, exportCustomersToExcel } from "../../components/Api/GmsCustomer";
import { getBranches } from "../../components/Api/GmsBranch";
import Swal from "sweetalert2";
import API_URL from '../../config';
import { Card, CardBody, CardTitle, Col, Container, Form, FormGroup, Input, Label, NavItem, NavLink, Row, TabContent, TabPane, Button, Table } from "reactstrap";
import Select from "react-select";
import classnames from "classnames";
import { Link } from "react-router-dom";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import Pagination from "rc-pagination";
import 'rc-pagination/assets/index.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission, 
  hasEditOrHigherPermission,
  hasDeletePermissionWithAdmin,
  isUserAdmin 
} from "../../components/Permission/PermissionService";
import * as XLSX from 'xlsx';
import { Download } from "lucide-react";
import CustomerProfileModal from "../../components/CustomerProfileModal";

const getDefaultForm = () => {
  // Get user's branch ID from localStorage if available
  const userBranchId = localStorage.getItem("userBranchId");
  
  return {
    customer_Id: null,
    customer_Code: "",
    customer_NameEn: "",
    customer_NameAr: "",
    customer_IsInternational: false,
    customer_AddressEn: "",
    customer_AddressAr: "",
    customer_Email: "",
    customer_ContactNo: "",
    customer_RepDetails: "",
    customer_Notes: "",
    customer_Active: true,
    customer_Branch_Id: userBranchId ? Number(userBranchId) : null,
    customer_CreditLimit: 0,
    customer_CreatedDate: new Date(),
    customer_CreatedBy: Number(localStorage.getItem("userId")) || 1,
    customer_ModifiedDate: new Date(),
    customer_ModifiedBy: Number(localStorage.getItem("userId")) || 1,
    customer_Type: 1, // 1 = Individual, 2 = Business
    company_Name: "",
    primaryContact_Name: "",
    primaryContact_Phone: "",
    customer_ID_Number: "", // New field for National ID or Business ID
    customer_DOB: "", // New field for Date of Birth
    customer_Salutation: "", // New field for Salutation
    customer_TRN: "", // New field for TRN
  };
};

const defaultValidation = {
  customer_NameEn: "",
  customer_NameAr: "",
  customer_AddressEn: "",
  customer_AddressAr: "",
  customer_Email: "",
  customer_ContactNo: "",
  customer_Branch_Id: "",
  customer_CreditLimit: "",
  company_Name: "",
  primaryContact_Name: "",
  primaryContact_Phone: "",
  customer_ID_Number: "",
  customer_DOB: "",
  customer_Salutation: "",
  customer_TRN: "",
};

const emptyFilter = {
  customer_Id: null,
  customer_Code: null,
  customer_NameEn: null,
  customer_NameAr: null,
  customer_IsInternational: null,
  customer_AddressEn: null,
  customer_AddressAr: null,
  customer_Email: null,
  customer_ContactNo: null,
  customer_RepDetails: null,
  customer_Notes: null,
  customer_Active: null,
  customer_Branch_Id: null,
  customer_CreditLimit: null,
  customer_CreatedDate: null,
  customer_CreatedBy: null,
  customer_ModifiedDate: null,
  customer_ModifiedBy: null,
  customer_Type: null,
  company_Name: null,
  customer_ID_Number: null,
  customer_DOB: null,
};

const FormSelect = ({ label, id, name, options, value, onChange, error, required = true, disabled = false }) => (
  <div className="form-group">
    <Label className="col-form-label" htmlFor={id}>
      {label} {required && '*'}
    </Label>
    <Select
      id={id}
      name={name}
      options={options}
      value={value}
      onChange={onChange}
      isClearable
      isSearchable
      placeholder={`Select ${label}`}
      classNamePrefix="select2-selection"
      className={error ? 'is-invalid' : ''}
      isDisabled={disabled}
    />
    {error && (
      <div className="invalid-feedback d-block">
        {error}
      </div>
    )}
  </div>
);

const FormInput = ({ label, id, name, type = "text", value, onChange, error, required = true, placeholder, disabled = false }) => (
  <div className="form-group">
    <Label className="col-form-label" htmlFor={id}>
      {label} {required && '*'}
    </Label>
    <Input
      type={type}
      className={`form-control ${error ? 'is-invalid' : ''}`}
      id={id}
      name={name}
      value={value || ''}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
    />
    {error && (
      <div className="invalid-feedback d-block">
        {error}
      </div>
    )}
  </div>
);

const FormCheckbox = ({ label, id, name, checked, onChange, disabled = false }) => (
  <div className="form-group mt-4">
    <div className="form-check form-switch">
      <input
        type="checkbox"
        className="form-check-input"
        id={id}
        name={name}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
      <label className="form-check-label" htmlFor={id}>
        {label === "Active" ? (checked ? "Active" : "Inactive") : label}
      </label>
    </div>
  </div>
);

const FormTextarea = ({ label, id, name, value, onChange, error, required = true, placeholder, rows = 3 }) => (
  <div className="form-group">
    <Label className="col-form-label" htmlFor={id}>
      {label} {required && '*'}
    </Label>
    <Input
      type="textarea"
      className={`form-control ${error ? 'is-invalid' : ''}`}
      id={id}
      name={name}
      value={value || ''}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      rows={rows}
    />
    {error && (
      <div className="invalid-feedback d-block">
        {error}
      </div>
    )}
  </div>
);

const FormRadio = ({ label, name, options, value, onChange, error, required = true }) => (
  <div className="form-group">
    <Label className="col-form-label">
      {label} {required && '*'}
    </Label>
    <div className="mt-2">
      {options.map((option) => (
        <div key={option.value} className="form-check form-check-inline">
          <Input
            type="radio"
            className="form-check-input"
            id={`${name}_${option.value}`}
            name={name}
            value={option.value}
            checked={value === option.value}
            onChange={onChange}
          />
          <Label className="form-check-label" htmlFor={`${name}_${option.value}`}>
            {option.label}
          </Label>
        </div>
      ))}
    </div>
    {error && (
      <div className="invalid-feedback d-block">
        {error}
      </div>
    )}
  </div>
);

export default function CustomerPage() {
  const [customers, setCustomers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState(getDefaultForm());
  const [filter, setFilter] = useState(emptyFilter);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [viewLoadingRow, setViewLoadingRow] = useState(null);
  const [editLoadingRow, setEditLoadingRow] = useState(null);
  const [activeTab, setActiveTab] = useState(1);

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsCustomer');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsCustomer');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsCustomer');
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsCustomer');
  const isAdmin = isUserAdmin();
  const [branchOptions, setBranchOptions] = useState([]);
  const [branchLoading, setBranchLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [validationErrors, setValidationErrors] = useState(defaultValidation);
  const [customerNameOptions, setCustomerNameOptions] = useState([]);
  const [customerSearchOptions, setCustomerSearchOptions] = useState([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  
  // Excel upload states
  const [excelFile, setExcelFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });

  const formatDateTime = (dateTimeStr) => {
    try {
      return dateTimeStr.split('T')[0];
    } catch (error) {
      return "N/A";
    }
  };

  const formatFullDateTime = (dateTimeStr) => {
    try {
      const date = new Date(dateTimeStr);
      return date.toLocaleString();
    } catch (error) {
      return "N/A";
    }
  };

  useEffect(() => {
    if (hasPermission) {
      loadBranches();
      loadCustomers();
      // Set default tab based on permissions
      if (isViewOnly) {
        setActiveTab(2); // Show list tab for view-only users
      }
    }
  }, [hasPermission, isViewOnly]);

  const loadBranches = async () => {
    try {
      setBranchLoading(true);
      const branchesData = await getBranches();
      const options = branchesData.map(branch => ({
        value: branch.branch_Id,
        label: branch.branch_NameEn
      }));
      setBranchOptions(options);
      setBranches(branchesData);
    } catch (error) {
      console.error("Error loading branches:", error);
      Swal.fire("Error", "Failed to load branches", "error");
    } finally {
      setBranchLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const customersData = await getCustomers(filter);
      setCustomers(customersData);
      setTotalCount(customersData.length);
      
      // Generate customer name options for dropdown
      const uniqueCustomerNames = [...new Set(customersData.map(customer => customer.customer_NameEn).filter(Boolean))];
      const nameOptions = uniqueCustomerNames.map(name => ({
        value: name,
        label: name
      }));
      setCustomerNameOptions(nameOptions);
    } catch (error) {
      console.error("Error loading customers:", error);
      Swal.fire("Error", "Failed to load customers", "error");
    } finally {
      setLoading(false);
    }
  };

  // Manual search function for customer name search
  const searchCustomerNamesManually = async (inputValue) => {
    console.log('🔍 searchCustomerNamesManually called with inputValue:', inputValue);
    
    if (!inputValue || inputValue.length < 3) {
      console.log('❌ Input too short, clearing options');
      setCustomerSearchOptions([]);
      return;
    }
    
    try {
      setCustomerSearchLoading(true);
      console.log('🚀 Making API call to searchCustomers with:', { inputValue, page: 1, pageSize: 20 });
      const response = await searchCustomers(inputValue, 1, 20);
      console.log('✅ API response received:', response);
      
      const formattedOptions = response.items.map(customer => ({
        value: customer.customer_NameEn,
        label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`,
        customer: customer
      }));
      
      console.log('📋 Formatted options:', formattedOptions);
      setCustomerSearchOptions(formattedOptions);
    } catch (error) {
      console.error('❌ Error searching customers:', error);
      setCustomerSearchOptions([]);
    } finally {
      setCustomerSearchLoading(false);
    }
  };

  const validateEmail = (email) => {
    if (!email || email.trim() === "") return "";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim()) ? "" : "Please enter a valid email address.";
  };

  const validateContactNumber = (contactNo) => {
    if (!contactNo || contactNo.trim() === "") return "";
    const phoneRegex = /^\d+$/;
    return phoneRegex.test(contactNo.trim()) ? "" : "Contact number can only contain numbers.";
  };

  // Excel upload functions
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.match(/\.(xlsx|xls)$/)) {
      Swal.fire("Error", "Please select an Excel file (.xlsx or .xls)", "error");
      return;
    }

    setExcelFile(file);
    parseExcelFile(file);
  };

  const parseExcelFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          Swal.fire("Error", "No data found in the Excel file", "error");
          return;
        }

        // Map Excel data to customer format
        const mappedData = jsonData.map((row, index) => {
          // Combine First Name + Last Name for customer name
          const firstName = row['First Name'] || '';
          const lastName = row['Last Name'] || '';
          const fullName = `${firstName} ${lastName}`.trim();

          // Get phone from Phone or MobilePhone column
          const phone = row['Phone'] || row['MobilePhone'] || '';

          // Get salutation
          const salutation = row['Salutation'] || '';

          return {
            customer_Id: null,
            customer_Code: "",
            customer_NameEn: fullName,
            customer_NameAr: "",
            customer_IsInternational: false,
            customer_AddressEn: "",
            customer_AddressAr: "",
            customer_Email: "",
            customer_ContactNo: phone,
            customer_RepDetails: "",
            customer_Notes: "",
            customer_Active: true,
            customer_Branch_Id: null,
            customer_CreditLimit: 0,
            customer_Type: 1, // Individual
            company_Name: "",
            primaryContact_Name: "",
            primaryContact_Phone: "",
            customer_ID_Number: "",
            customer_DOB: null,
            customer_Salutation: salutation,
            customer_CreatedDate: new Date(),
            customer_CreatedBy: Number(localStorage.getItem("userId")) || 1,
            customer_ModifiedDate: new Date(),
            customer_ModifiedBy: Number(localStorage.getItem("userId")) || 1,
            _rowIndex: index + 1 // For error tracking
          };
        }).filter(customer => customer.customer_NameEn && customer.customer_ContactNo);

        setParsedData(mappedData);
        Swal.fire("Success", `Parsed ${mappedData.length} customers from Excel file`, "success");
      } catch (error) {
        console.error("Error parsing Excel file:", error);
        Swal.fire("Error", "Failed to parse Excel file. Please check the format.", "error");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleBulkImport = async () => {
    if (parsedData.length === 0) {
      Swal.fire("Error", "No data to import", "error");
      return;
    }

    const result = await Swal.fire({
      title: 'Confirm Import',
      text: `Are you sure you want to import ${parsedData.length} customers?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Import',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    try {
      setImportLoading(true);
      setImportProgress({ current: 0, total: parsedData.length });

      const response = await bulkImportCustomers(parsedData);
      
      setImportLoading(false);
      setImportProgress({ current: 0, total: 0 });
      
      if (response && response.ids) {
        Swal.fire("Success", `Successfully imported ${parsedData.length} customers!`, "success");
        setParsedData([]);
        setExcelFile(null);
        loadCustomers(); // Refresh the customer list
      } else {
        Swal.fire("Error", response.message || "Import failed", "error");
      }
    } catch (error) {
      console.error("Bulk import error:", error);
      setImportLoading(false);
      setImportProgress({ current: 0, total: 0 });
      Swal.fire("Error", "Failed to import customers. Please try again.", "error");
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : (type === 'radio' ? parseInt(value) : value);
    
    setForm(prev => {
      const updatedForm = {
        ...prev,
        [name]: newValue
      };
      
      // If customer type changes to Individual, clear company name
      if (name === 'customer_Type' && newValue === 1) {
        updatedForm.company_Name = "";
      }
      
      // If customer type changes to Business, clear DOB
      if (name === 'customer_Type' && newValue === 2) {
        updatedForm.customer_DOB = "";
      }
      
      return updatedForm;
    });
    
    // Clear validation error for this field
    setValidationErrors(prev => ({
      ...prev,
      [name]: ""
    }));

    // Real-time validation for email and contact number
    if (name === 'customer_Email') {
      const emailError = validateEmail(value);
      setValidationErrors(prev => ({
        ...prev,
        customer_Email: emailError
      }));
    }
    
    if (name === 'customer_ContactNo') {
      const contactError = validateContactNumber(value);
      setValidationErrors(prev => ({
        ...prev,
        customer_ContactNo: contactError
      }));
    }
  };

  const handleSelectChange = (name, selectedOption) => {
    setForm(prev => ({
      ...prev,
      [name]: selectedOption ? selectedOption.value : null
    }));
    // Clear validation error for this field
    setValidationErrors(prev => ({
      ...prev,
      [name]: ""
    }));
  };

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilter(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (value === '' ? null : value)
    }));
  };

  const handleFilterSelectChange = (name, selectedOption) => {
    setFilter(prev => ({
      ...prev,
      [name]: selectedOption ? selectedOption.value : null
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    let hasErrors = false;
    const newValidationErrors = { ...defaultValidation };

    // Required field validations - Only Customer Name (English) and Contact Number are mandatory
    if (!form.customer_NameEn || form.customer_NameEn.trim() === "") {
      newValidationErrors.customer_NameEn = "Customer Name (English) is required.";
      hasErrors = true;
    }
    if (!form.customer_ContactNo || form.customer_ContactNo.trim() === "") {
      newValidationErrors.customer_ContactNo = "Contact Number is required.";
      hasErrors = true;
    }

    // Email validation (if provided)
    if (form.customer_Email && form.customer_Email.trim() !== "") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.customer_Email.trim())) {
        newValidationErrors.customer_Email = "Please enter a valid email address.";
        hasErrors = true;
      }
    }

    // Contact number validation (if provided)
    if (form.customer_ContactNo && form.customer_ContactNo.trim() !== "") {
      const phoneRegex = /^\d+$/;
      if (!phoneRegex.test(form.customer_ContactNo.trim())) {
        newValidationErrors.customer_ContactNo = "Contact number can only contain numbers.";
        hasErrors = true;
      }
    }

    setValidationErrors(newValidationErrors);

    if (hasErrors) {
      Swal.fire("Validation Error", "Please Enter All The Details.", "warning");
      return;
    }

    try {
      setLoading(true);
      
      const customerData = {
        ...form,
        customer_DOB: form.customer_DOB === "" ? null : form.customer_DOB,
        customer_CreatedBy: Number(localStorage.getItem("userId")) || 1,
        customer_ModifiedBy: Number(localStorage.getItem("userId")) || 1,
      };
      
      // Ensure active=true by default for users without delete permission
      if (!hasDeletePermission) {
        customerData.customer_Active = true;
      }

      console.log("Saving customer data:", customerData);
      const response = await saveCustomer(customerData);
      console.log("API response:", response);
      
      // Check if response has id (success) or error message
      if (response && response.id) {
        Swal.fire("Success", response.message || "Customer saved successfully", "success");
        resetForm();
        loadCustomers();
      } else {
        Swal.fire("Error", response.message || "Failed to save customer", "error");
      }
    } catch (error) {
      console.error("Error saving customer:", error);
      Swal.fire("Error", "Failed to save customer", "error");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm(getDefaultForm());
    setIsEditing(false);
    setValidationErrors(defaultValidation);
  };

  const handleEdit = (customer) => {
    setForm({
      ...customer,
      customer_Branch_Id: customer.customer_Branch_Id
    });
    setIsEditing(true);
    setActiveTab(1);
    setValidationErrors(defaultValidation);
  };

  const handleView = (customer) => {
    setSelectedCustomer(customer);
    setProfileModalOpen(true);
  };

  const handleApplyFilter = () => {
    setPage(1);
    loadCustomers();
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);
      // Clean filter - remove empty strings and convert to proper format
      const cleanFilter = {
        ...(filter.customer_Id !== null && filter.customer_Id !== undefined && { customer_Id: filter.customer_Id }),
        ...(filter.customer_NameEn && filter.customer_NameEn.trim() !== '' && { customer_NameEn: filter.customer_NameEn }),
        ...(filter.customer_ContactNo && filter.customer_ContactNo.trim() !== '' && { customer_ContactNo: filter.customer_ContactNo }),
        ...(filter.customer_IsInternational !== null && filter.customer_IsInternational !== undefined && { customer_IsInternational: filter.customer_IsInternational }),
        ...(filter.customer_Active !== null && filter.customer_Active !== undefined && { customer_Active: filter.customer_Active }),
        ...(filter.customer_Branch_Id !== null && filter.customer_Branch_Id !== undefined && { customer_Branch_Id: filter.customer_Branch_Id }),
        ...(filter.company_Name && filter.company_Name.trim() !== '' && { company_Name: filter.company_Name })
      };
      
      await exportCustomersToExcel(cleanFilter);
      Swal.fire("Success", "Customer report exported successfully", "success");
    } catch (error) {
      console.error("Error exporting customers:", error);
      Swal.fire("Error", "Failed to export customer report", "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setPage(page);
    loadCustomers();
  };

  const getBranchName = (branchId) => {
    const branch = branches.find(b => b.branch_Id === branchId);
    return branch ? branch.branch_NameEn : 'N/A';
  };

  const paginatedCustomers = customers.slice((page - 1) * pageSize, page * pageSize);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Customer Management" breadcrumbItem="Spa" />
          
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="wizard clearfix">
                    <div className="steps clearfix">
                      <ul>
                        {!isViewOnly && (
                          <NavItem className={classnames({ current: activeTab === 1 })}>
                            <NavLink
                              className={classnames({ current: activeTab === 1 })}
                              onClick={() => setActiveTab(1)}
                            >
                              <span className="number">1.</span> {isEditing ? "Edit Customer" : "Add Customer"}
                            </NavLink>
                          </NavItem>
                        )}
                        <NavItem className={classnames({ current: activeTab === 2 })}>
                          <NavLink
                            className={classnames({ active: activeTab === 2 })}
                            onClick={() => setActiveTab(2)}
                          >
                            <span className="number">2.</span> Customer List
                          </NavLink>
                        </NavItem>
                        {!isViewOnly && (
                          <NavItem className={classnames({ current: activeTab === 3 })}>
                            <NavLink
                              className={classnames({ active: activeTab === 3 })}
                              onClick={() => setActiveTab(3)}
                            >
                              <span className="number">3.</span> Excel Import
                            </NavLink>
                          </NavItem>
                        )}
                      </ul>
                    </div>

                    <div className="content clearfix">
                      <TabContent activeTab={activeTab} className="body">
                        <TabPane tabId={1}>
                      <Form onSubmit={handleSubmit}>
                        <Row>
                          <Col md={1}>
                            <div className="form-group">
                              <Label className="col-form-label" htmlFor="customer_Salutation">Salutation</Label>
                              <Select
                                id="customer_Salutation"
                                name="customer_Salutation"
                                options={[
                                  { value: "", label: "Select" },
                                  { value: "Mr.", label: "Mr." },
                                  { value: "Ms.", label: "Ms." },
                                  { value: "Mrs.", label: "Mrs." },
                                  { value: "Dr.", label: "Dr." }
                                ]}
                                value={form.customer_Salutation ? { value: form.customer_Salutation, label: form.customer_Salutation } : null}
                                onChange={(option) => handleSelectChange('customer_Salutation', option)}
                                isClearable
                                isSearchable
                                placeholder="Select"
                                classNamePrefix="select2-selection"
                                isDisabled={isViewOnly}
                                styles={{
                                  control: (provided, state) => ({
                                    ...provided,
                                    backgroundColor: 'white',
                                    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
                                    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
                                    minHeight: '38px',
                                    width: '100%',
                                    maxWidth: '120px'
                                  }),
                                  menu: (provided) => ({
                                    ...provided,
                                    backgroundColor: 'white',
                                    zIndex: 9999,
                                    border: '1px solid #ced4da',
                                    boxShadow: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)',
                                    width: '120px'
                                  }),
                                  option: (provided, state) => ({
                                    ...provided,
                                    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
                                    color: state.isSelected ? 'white' : '#495057',
                                    '&:hover': {
                                      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
                                    },
                                  }),
                                  singleValue: (provided) => ({
                                    ...provided,
                                    color: '#495057'
                                  }),
                                  placeholder: (provided) => ({
                                    ...provided,
                                    color: '#6c757d'
                                  })
                                }}
                              />
                            </div>
                          </Col>
                          <Col md={5}>
                            <FormInput
                              label="Customer Name (English)"
                              id="customer_NameEn"
                              name="customer_NameEn"
                              value={form.customer_NameEn}
                              onChange={handleInputChange}
                              required={true}
                              placeholder="Enter customer name in English"
                              error={validationErrors.customer_NameEn}
                              disabled={isViewOnly}
                            />
                          </Col>
                          <Col md={6}>
                            <FormInput
                              label="Customer Name (Arabic)"
                              id="customer_NameAr"
                              name="customer_NameAr"
                              value={form.customer_NameAr}
                              onChange={handleInputChange}
                              required={false}
                              placeholder="Enter customer name in Arabic"
                              error={validationErrors.customer_NameAr}
                              disabled={isViewOnly}
                            />
                          </Col>
                        </Row>

                        <Row>
                          <Col md={6}>
                            <FormInput
                              label="Email"
                              id="customer_Email"
                              name="customer_Email"
                              type="email"
                              value={form.customer_Email}
                              onChange={handleInputChange}
                              required={false}
                              placeholder="Enter email address"
                              error={validationErrors.customer_Email}
                              disabled={isViewOnly}
                            />
                          </Col>
                          <Col md={6}>
                            <FormInput
                              label="Contact Number"
                              id="customer_ContactNo"
                              name="customer_ContactNo"
                              type="number"
                              value={form.customer_ContactNo}
                              onChange={handleInputChange}
                              required={true}
                              placeholder="Enter contact number"
                              error={validationErrors.customer_ContactNo}
                              disabled={isViewOnly}
                            />
                          </Col>
                        </Row>

                        <Row>
                          <Col md={6}>
                            <FormInput
                              label="TRN"
                              id="customer_TRN"
                              name="customer_TRN"
                              value={form.customer_TRN}
                              onChange={handleInputChange}
                              required={false}
                              placeholder="Enter TRN (Tax Registration Number)"
                              error={validationErrors.customer_TRN}
                              disabled={isViewOnly}
                            />
                          </Col>
                        </Row>

                        <Row>
                          <Col md={6}>
                            <FormRadio
                              label="Customer Type"
                              name="customer_Type"
                              options={[
                                { value: 1, label: "Individual" },
                                { value: 2, label: "Business" }
                              ]}
                              value={form.customer_Type}
                              onChange={handleInputChange}
                              required={true}
                            />
                          </Col>
                          <Col md={6}>
                            {form.customer_Type === 2 && (
                              <FormInput
                                label="Company Name"
                                id="company_Name"
                                name="company_Name"
                                value={form.company_Name}
                                onChange={handleInputChange}
                                required={false}
                                placeholder="Enter company name"
                                error={validationErrors.company_Name}
                                disabled={isViewOnly}
                              />
                            )}
                          </Col>
                        </Row>

                        <Row>
                          <Col md={6}>
                            <FormInput
                              label={form.customer_Type === 1 ? "National ID Number" : "Business ID Number"}
                              id="customer_ID_Number"
                              name="customer_ID_Number"
                              value={form.customer_ID_Number}
                              onChange={handleInputChange}
                              required={false}
                              placeholder={form.customer_Type === 1 ? "Enter national ID number" : "Enter business ID number"}
                              error={validationErrors.customer_ID_Number}
                              disabled={isViewOnly}
                            />
                          </Col>
                          <Col md={6}>
                            {form.customer_Type === 1 && (
                              <FormInput
                                label="Date of Birth"
                                id="customer_DOB"
                                name="customer_DOB"
                                type="date"
                                value={form.customer_DOB}
                                onChange={handleInputChange}
                                required={false}
                                error={validationErrors.customer_DOB}
                                disabled={isViewOnly}
                              />
                            )}
                          </Col>
                        </Row>

                        <Row>
                          <Col md={6}>
                            <FormInput
                              label="Primary Contact Name"
                              id="primaryContact_Name"
                              name="primaryContact_Name"
                              value={form.primaryContact_Name}
                              onChange={handleInputChange}
                              required={false}
                              placeholder="Enter primary contact name"
                              error={validationErrors.primaryContact_Name}
                              disabled={isViewOnly}
                            />
                          </Col>
                          <Col md={6}>
                            <FormInput
                              label="Primary Contact Phone"
                              id="primaryContact_Phone"
                              name="primaryContact_Phone"
                              type="number"
                              value={form.primaryContact_Phone}
                              onChange={handleInputChange}
                              required={false}
                              placeholder="Enter primary contact phone"
                              error={validationErrors.primaryContact_Phone}
                              disabled={isViewOnly}
                            />
                          </Col>
                        </Row>

                        <Row>
                          <Col md={6}>
                            <FormInput
                              label="Address (English)"
                              id="customer_AddressEn"
                              name="customer_AddressEn"
                              value={form.customer_AddressEn}
                              onChange={handleInputChange}
                              required={false}
                              placeholder="Enter address in English"
                              error={validationErrors.customer_AddressEn}
                              disabled={isViewOnly}
                            />
                          </Col>
                          <Col md={6}>
                            <FormInput
                              label="Address (Arabic)"
                              id="customer_AddressAr"
                              name="customer_AddressAr"
                              value={form.customer_AddressAr}
                              onChange={handleInputChange}
                              required={false}
                              placeholder="Enter address in Arabic"
                              error={validationErrors.customer_AddressAr}
                              disabled={isViewOnly}
                            />
                          </Col>
                        </Row>

                        <Row>
                          <Col md={6}>
                            <FormSelect
                              label="Branch"
                              id="customer_Branch_Id"
                              name="customer_Branch_Id"
                              options={branchOptions}
                              value={branchOptions.find(option => option.value === form.customer_Branch_Id) || null}
                              onChange={(option) => handleSelectChange('customer_Branch_Id', option)}
                              required={false}
                              error={validationErrors.customer_Branch_Id}
                              disabled={!!localStorage.getItem("userBranchId")}
                            />
                          </Col>
                          <Col md={6}>
                            <FormInput
                              label="Credit Limit"
                              id="customer_CreditLimit"
                              name="customer_CreditLimit"
                              type="number"
                              value={form.customer_CreditLimit}
                              onChange={handleInputChange}
                              required={false}
                              placeholder="Enter credit limit"
                              error={validationErrors.customer_CreditLimit}
                              disabled={isViewOnly}
                            />
                          </Col>
                        </Row>

                        {form.customer_Type === 2 && (
                          <Row>
                            <Col md={12}>
                              <FormTextarea
                                label="Customer Representative Details"
                                id="customer_RepDetails"
                                name="customer_RepDetails"
                                value={form.customer_RepDetails}
                                onChange={handleInputChange}
                                required={false}
                                placeholder="Enter customer representative details"
                                rows={3}
                              />
                            </Col>
                          </Row>
                        )}

                        <Row>
                          <Col md={12}>
                            <FormTextarea
                              label="Notes"
                              id="customer_Notes"
                              name="customer_Notes"
                              value={form.customer_Notes}
                              onChange={handleInputChange}
                              required={false}
                              placeholder="Enter additional notes"
                              rows={3}
                            />
                          </Col>
                        </Row>

                        <Row>
                          <Col md={4}>
                            <FormCheckbox
                              label="International Customer"
                              id="customer_IsInternational"
                              name="customer_IsInternational"
                              checked={form.customer_IsInternational}
                              onChange={handleInputChange}
                            />
                          </Col>
                          {hasDeletePermission && (
                            <Col md={4}>
                              <FormCheckbox
                                label="Active"
                                id="customer_Active"
                                name="customer_Active"
                                checked={form.customer_Active}
                                onChange={handleInputChange}
                              />
                            </Col>
                          )}
                        </Row>

                        <Row>
                          <Col md={12}>
                            <div className="mt-4">
                              <Button type="submit" color="success" disabled={loading}>
                                {loading ? "Saving..." : (isEditing ? "Update" : "Save Customer")}
                              </Button>
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    </TabPane>

                                            <TabPane tabId={2}>
                      <Row>
                        <Col md={12}>
                          <div className="d-flex align-items-center justify-content-between mb-3">
                            <div className="d-flex align-items-center gap-2">
                              <h5 className="mb-0 fw-bold">Filter Customers</h5>
                              <Button
                                color="primary"
                                size="sm"
                                onClick={() => setShowFilters(!showFilters)}
                              >
                                {showFilters ? "Hide Filters" : "Show Filters"}
                              </Button>
                            </div>
                            <Button 
                              color="success" 
                              size="sm"
                              onClick={handleExportExcel}
                              disabled={loading || totalCount === 0}
                            >
                              <Download className="me-1" size={16} />
                              Export Excel
                            </Button>
                          </div>

                          {showFilters && (
                            <Card className="mb-3">
                              <CardBody>
                                <Row>
                                  <Col md={3}>
                                    <div className="form-group">
                                      <Label className="col-form-label">
                                        Customer Name
                                      </Label>
                                      <Select
                                        id="filter_customer_NameEn"
                                        name="customer_NameEn"
                                        options={customerSearchOptions}
                                        value={customerNameOptions.find(option => option.value === filter.customer_NameEn) || null}
                                        onChange={(option) => handleFilterSelectChange('customer_NameEn', option)}
                                        isClearable
                                        isSearchable
                                        placeholder="Type at least 3 characters to search..."
                                        classNamePrefix="select2-selection"
                                        isLoading={customerSearchLoading}
                                        noOptionsMessage={() => "Type at least 3 characters to search"}
                                        loadingMessage={() => "Searching customers..."}
                                        onInputChange={(inputValue) => {
                                          console.log('🔤 Input changed:', inputValue);
                                          
                                          // Clear previous timeout
                                          if (searchTimeout) {
                                            clearTimeout(searchTimeout);
                                          }
                                          
                                          // Set new timeout
                                          const timeout = setTimeout(() => {
                                            searchCustomerNamesManually(inputValue);
                                          }, 300);
                                          setSearchTimeout(timeout);
                                          
                                          return inputValue;
                                        }}
                                        onMenuOpen={() => console.log('📂 Menu opened')}
                                        onMenuClose={() => console.log('📁 Menu closed')}
                                      />
                                    </div>
                                  </Col>
                                  <Col md={3}>
                                    <FormInput
                                      label="Contact Number"
                                      id="filter_customer_ContactNo"
                                      name="customer_ContactNo"
                                      value={filter.customer_ContactNo}
                                      onChange={handleFilterChange}
                                      required={false}
                                      placeholder="Filter by contact number"
                                    />
                                  </Col>
                                  <Col md={2}>
                                    <FormSelect
                                      label="International"
                                      id="filter_customer_IsInternational"
                                      name="customer_IsInternational"
                                      options={[
                                        { value: true, label: "Yes" },
                                        { value: false, label: "No" }
                                      ]}
                                      value={filter.customer_IsInternational !== null ? 
                                        { value: filter.customer_IsInternational, label: filter.customer_IsInternational ? "Yes" : "No" } : null}
                                      onChange={(option) => handleFilterSelectChange('customer_IsInternational', option)}
                                      required={false}
                                    />
                                  </Col>
                                  <Col md={2}>
                                    <FormSelect
                                      label="Status"
                                      id="filter_customer_Active"
                                      name="customer_Active"
                                      options={[
                                        { value: true, label: "Active" },
                                        { value: false, label: "Inactive" }
                                      ]}
                                      value={filter.customer_Active !== null ? 
                                        { value: filter.customer_Active, label: filter.customer_Active ? "Active" : "Inactive" } : null}
                                      onChange={(option) => handleFilterSelectChange('customer_Active', option)}
                                      required={false}
                                    />
                                  </Col>
                                  <Col md={2}>
                                    <FormSelect
                                      label="Branch"
                                      id="filter_customer_Branch_Id"
                                      name="customer_Branch_Id"
                                      options={branchOptions}
                                      value={branchOptions.find(option => option.value === filter.customer_Branch_Id) || null}
                                      onChange={(option) => handleFilterSelectChange('customer_Branch_Id', option)}
                                      required={false}
                                    />
                                  </Col>
                                </Row>
                                <Row className="mt-3">
                                  <Col md={3}>
                                    <FormSelect
                                      label="Customer Type"
                                      id="filter_customer_Type"
                                      name="customer_Type"
                                      options={[
                                        { value: 1, label: "Individual" },
                                        { value: 2, label: "Business" }
                                      ]}
                                      value={filter.customer_Type !== null ? 
                                        { value: filter.customer_Type, label: filter.customer_Type === 2 ? "Business" : "Individual" } : null}
                                      onChange={(option) => handleFilterSelectChange('customer_Type', option)}
                                      required={false}
                                    />
                                  </Col>
                                  <Col md={3}>
                                    <FormInput
                                      label="Company Name"
                                      id="filter_company_Name"
                                      name="company_Name"
                                      value={filter.company_Name}
                                      onChange={handleFilterChange}
                                      required={false}
                                      placeholder="Filter by company name"
                                    />
                                  </Col>
                                </Row>
                                <Row>
                                  <Col md={12}>
                                    <Button color="primary" onClick={handleApplyFilter}>
                                      Apply Filter
                                    </Button>
                                  </Col>
                                </Row>
                              </CardBody>
                            </Card>
                          )}

                          <div className="table-responsive">
                            <Table className="table table-bordered table-striped">
                              <thead>
                                <tr>
                                  <th>Sr. No.</th>
                                  <th>Name</th>
                                  <th>Address</th>
                                  <th>International</th>
                                  <th>Email</th>
                                  <th>Contact Number</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {loading ? (
                                  <tr>
                                    <td colSpan="7" className="text-center">
                                      Loading...
                                    </td>
                                  </tr>
                                ) : paginatedCustomers.length === 0 ? (
                                  <tr>
                                    <td colSpan="7" className="text-center">
                                      No customers found
                                    </td>
                                  </tr>
                                ) : (
                                  paginatedCustomers.map((customer, index) => (
                                    <tr key={customer.customer_Id}>
                                      <td>{(page - 1) * pageSize + index + 1}</td>
                                      <td>{customer.customer_NameEn}</td>
                                      <td>{customer.customer_AddressEn}</td>
                                      <td>
                                        <span className={`badge ${customer.customer_IsInternational ? 'bg-success' : 'bg-secondary'}`}>
                                          {customer.customer_IsInternational ? 'Yes' : 'No'}
                                        </span>
                                      </td>
                                      <td>{customer.customer_Email}</td>
                                      <td>{customer.customer_ContactNo}</td>
                                      <td>
                                        <div className="d-flex gap-2">
                                          {hasEditOrHigher && (
                                            <Button
                                              color="info"
                                              size="sm"
                                              onClick={() => handleEdit(customer)}
                                              disabled={editLoadingRow === customer.customer_Id}
                                            >
                                              Edit
                                            </Button>
                                          )}
                                          <Button
                                            color="primary"
                                            size="sm"
                                            onClick={() => handleView(customer)}
                                            disabled={viewLoadingRow === customer.customer_Id}
                                          >
                                            View
                                          </Button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </Table>
                          </div>

                          {totalCount > pageSize && (
                            <div className="d-flex justify-content-between align-items-center mt-3">
                              <div className="text-muted">
                                Showing {Math.min((page - 1) * pageSize + 1, totalCount)} to{" "}
                                {Math.min(page * pageSize, totalCount)} of {totalCount} entries
                              </div>
                              <div className="pagination-wrapper">
                                <Pagination
                                  className="ant-pagination"
                                  current={page}
                                  total={totalCount}
                                  pageSize={pageSize}
                                  onChange={handlePageChange}
                                  showTitle={false}
                                />
                              </div>
                            </div>
                          )}
                        </Col>
                      </Row>
                        </TabPane>
                        
                        <TabPane tabId={3}>
                          <Row>
                            <Col md={12}>
                              <Card>
                                <CardBody>
                                  <h5 className="card-title mb-4">Excel Import</h5>
                                  
                                  <div className="mb-4">
                                    <Label for="excelFile" className="form-label">
                                      Select Excel File (.xlsx or .xls)
                                    </Label>
                                    <Input
                                      type="file"
                                      id="excelFile"
                                      accept=".xlsx,.xls"
                                      onChange={handleFileUpload}
                                      className="form-control"
                                    />
                                    <small className="form-text text-muted">
                                      Excel file should contain columns: First Name, Last Name, Phone/MobilePhone, Salutation
                                    </small>
                                  </div>

                                  {parsedData.length > 0 && (
                                    <div className="mb-4">
                                      <h6>Preview ({parsedData.length} customers found):</h6>
                                      <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                        <Table className="table table-bordered table-striped table-sm">
                                          <thead className="table-dark">
                                            <tr>
                                              <th>#</th>
                                              <th>Name</th>
                                              <th>Phone</th>
                                              <th>Salutation</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {parsedData.slice(0, 10).map((customer, index) => (
                                              <tr key={index}>
                                                <td>{index + 1}</td>
                                                <td>{customer.customer_NameEn}</td>
                                                <td>{customer.customer_ContactNo}</td>
                                                <td>{customer.customer_Salutation}</td>
                                              </tr>
                                            ))}
                                            {parsedData.length > 10 && (
                                              <tr>
                                                <td colSpan="4" className="text-center text-muted">
                                                  ... and {parsedData.length - 10} more customers
                                                </td>
                                              </tr>
                                            )}
                                          </tbody>
                                        </Table>
                                      </div>
                                    </div>
                                  )}

                                  {importLoading && (
                                    <div className="mb-4">
                                      <div className="d-flex align-items-center">
                                        <div className="spinner-border spinner-border-sm me-2" role="status">
                                          <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <span>Importing customers...</span>
                                      </div>
                                    </div>
                                  )}

                                  <div className="d-flex gap-2">
                                    <Button
                                      color="primary"
                                      onClick={handleBulkImport}
                                      disabled={parsedData.length === 0 || importLoading}
                                    >
                                      {importLoading ? 'Importing...' : `Import ${parsedData.length} Customers`}
                                    </Button>
                                    
                                    {parsedData.length > 0 && (
                                      <Button
                                        color="secondary"
                                        onClick={() => {
                                          setParsedData([]);
                                          setExcelFile(null);
                                        }}
                                        disabled={importLoading}
                                      >
                                        Clear
                                      </Button>
                                    )}
                                  </div>
                                </CardBody>
                              </Card>
                            </Col>
                          </Row>
                        </TabPane>
                      </TabContent>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Customer Profile Modal */}
      <CustomerProfileModal
        isOpen={profileModalOpen}
        toggle={() => setProfileModalOpen(false)}
        customer={selectedCustomer}
        customerId={selectedCustomer?.customer_Id || null}
      />
    </React.Fragment>
  );
}
